import { useUser } from '@store/user';
import { clearAuthStorage, isTokenExpired } from '@utils/tokenUtils';
import { useEffect, useRef, useState, type ReactNode } from 'react';

interface UserHydrationProps {
  children: ReactNode;
}

export const UserHydration = ({ children }: UserHydrationProps) => {
  const { user, fetchUserById } = useUser();
  const [isHydrating, setIsHydrating] = useState(true);
  const attemptedRef = useRef(false);

  const userRef = useRef(user);
  userRef.current = user;

  const fetchRef = useRef(fetchUserById);
  fetchRef.current = fetchUserById;

  useEffect(() => {
    if (attemptedRef.current) {
      setIsHydrating(false);
      return;
    }
    attemptedRef.current = true;

    const hydrateUser = async () => {
      try {
        const userId = sessionStorage.getItem('userId');
        const authToken = sessionStorage.getItem('authToken');
        const tokenExpiresAt = sessionStorage.getItem('tokenExpiresAt');

        if (authToken && isTokenExpired(tokenExpiresAt)) {
          clearAuthStorage();
          return;
        }

        const currentUser = userRef.current;
        const hasMinimalUser =
          Boolean(currentUser?.id) &&
          Boolean(currentUser?.authToken) &&
          Boolean(currentUser?.personalInfo?.fullName);

        if (userId && authToken && !hasMinimalUser) {
          await fetchRef.current(userId);
        }
      } catch {
        // Silent fail on hydration
      } finally {
        setIsHydrating(false);
      }
    };

    hydrateUser();
  }, []);

  if (isHydrating) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
